<script setup lang="ts">
import { computed } from 'vue'
import SearchInput from '@/components/search/SearchInput.vue'
import StrategyFilterMenu from './StrategyFilterMenu.vue'
import { buildFilters } from './strategy_filters'
import { useStrategyTags } from './useStrategyTags'
import type { StrategyFilter, StrategyFilterSelection } from './strategy_types'

const props = defineProps<{ selection: StrategyFilterSelection }>()

const emit = defineEmits<{
  'update:filter': [update: { field: keyof StrategyFilterSelection; value: string | string[] | null }]
}>()

const { tags, loadingOptions, optionsError } = useStrategyTags()

const filters = computed(() => buildFilters(props.selection, tags.value))

function triggerLabel(filter: StrategyFilter) {
  return `${filter.label}, ${filter.valueLabel || filter.emptyText}`
}

function isClearable(filter: StrategyFilter) {
  return Boolean(filter.valueLabel) || filter.field !== 'grades'
}
</script>

<template>
  <div class="mb-4 flex flex-wrap border border-gray-400 bg-green-100">
    <div class="w-full flex-none p-3 md:w-auto md:flex-[2_1_0%]">
      <SearchInput
        :initial-query="selection.query"
        hide-button
        input-id="strategy-search-input"
        @query:changed="emit('update:filter', { field: 'query', value: $event })"
      />
    </div>

    <div v-for="filter in filters" :key="filter.field" class="p-3">
      <span class="mb-1 block text-text-sm font-bold text-gray-800">
        {{ filter.label }}
      </span>
      <StrategyFilterMenu
        :filter="filter"
        :trigger-label="triggerLabel(filter)"
        :clearable="isClearable(filter)"
        @select="emit('update:filter', { field: filter.field, value: $event })"
      />
    </div>

    <div class="flex-1 self-center p-3 text-right">
      <router-link
        :to="{ name: 'StrategyIndex' }"
        class="whitespace-nowrap text-blue-400 underline-offset-4 hover:underline focus:outline-offset-4"
      >
        View strategy index
      </router-link>
    </div>

    <p v-if="loadingOptions" role="status" class="w-full flex-none px-3 pb-3 text-text-md text-gray-800">
      Loading options...
    </p>
    <p v-else-if="optionsError" role="alert" class="w-full flex-none px-3 pb-3 text-text-md text-gray-900">
      {{ optionsError }}
    </p>
  </div>
</template>
